import { TextLink } from "@vds/buttons";
import { Modal, ModalBody, ModalFooter, ModalTitle } from "@vds/modals";
import { Loader } from "@vds/loaders";
import { useEffect, useState } from "react";
import { useWindowSize } from "../../../hooks/viewportHooks";
import { breakpoints } from "../../../../styles/breakpoints";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { submitAdvancedConfiguration } from "../../../utility/configUtils";
import { MainSection } from "./MainSection";
import { Title } from "@vds/typography";
import { Icon } from "@vds/icons";
import { clearAdvancedConfigAppearances } from "../../../../store/actions";
import { Box } from "@mui/material";
import { ACTION_TYPES } from "../../../../pages/Polygon/constants";

const AdvancedConfigModal = styled(Modal)`
  @media (min-width: ${breakpoints.tablet}) {
    min-width: 33vw;
  }

  .vision_add-config-submit-button {
    background: black;
    color: #ffff;
  }

  .vision_add-config-submit-button-disabled {
    background: #fff;
    color: grey;
    cursor: not-allowed;
    pointer-events: none;
  }

  .vision_add-config-reset-button {
    background: black;
    color: #ffff;
    width: max-content;
    border-radius: 50px;
  }

  .vision_add-config-reset-button-disabled {
    background: #fff;
    color: grey;
    width: max-content;
    cursor: not-allowed;
    pointer-events: none;
    border: 1px solid grey;
    border-radius: 50px;
  }
`;

const AdvancedConfiguration = ({
  schemaData,
  ids,
  advancedData,
  setAdvancedData,
  resetFunc,
  action,
  setIsAdvanceConfigTampered,
  isAdvanceConfigTampered,
  cvAppName,
  ErrorInFormInput,
  setErrorInFormInput,
  advanceConfigRef,
  originalDeployConfig,
  siteId,
  isRoiListFetching = false
}) => {
  const dispatch = useDispatch();
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const { width: viewportWidth } = useWindowSize();
  const isModalFullScreen = (viewportWidth ?? 1400) < parseInt(breakpoints.tablet);
  const [isOpen, setIsOpen] = useState(false);
  const [isClick, setIsClick] = useState(false);
  const [errorInAdvanceConfig, setErrorInAdvanceConfig] = useState([]);

  const handleCloseOrCancel = () => {
    if (action === ACTION_TYPES.CREATE) {
      dispatch(clearAdvancedConfigAppearances());
      resetFunc();
    } else if (action === ACTION_TYPES.UPDATE) {
      if (originalDeployConfig?.enrolled_appearance_parameters) {
        setAdvancedData({
          config: { "deploy-parameters": { ...originalDeployConfig } }
        });
      } else {
        dispatch(clearAdvancedConfigAppearances());
        resetFunc();
      }
      setIsAdvanceConfigTampered(false);
    }
    setIsOpen(false);
  };

  const buttons = {
    primary: {
      width: "100%",
      "data-testid": "advancedConfig_cancelButton",
      children: "Cancel",
      use: "secondary",
      onClick: () => {
        setIsOpen(false);
      },
    },
    close: {
      width: "100%",
      "data-testid": "advancedConfig_resetToDefaultButton",
      children: "Submit",
      className:
        errorInAdvanceConfig.length === 0
          ? "vision_add-config-submit-button"
          : "vision_add-config-submit-button-disabled",
      onClick: () => {
        submitAdvancedConfiguration(
          advancedData,
          activeSession,
          ids,
          setIsOpen
        );
      },
    },
  };

  const resetButton = {
    close: {
      width: "100%",
      children: "Reset to default",
      className: !isAdvanceConfigTampered
        ? "vision_add-config-reset-button-disabled"
        : "vision_add-config-reset-button",
      onClick: () => {
        resetFunc();
        // setIsOpen(false);
      },
    },
  };

  useEffect(() => {
    const advance_params = Object.keys(
      advancedData?.config?.["deploy-parameters"]
    );

    if (
      ErrorInFormInput &&
      Array.isArray(ErrorInFormInput) &&
      ErrorInFormInput.length > 0
    ) {
      if (
        advance_params &&
        Array.isArray(advance_params) &&
        advance_params.length > 0
      ) {
        let errorInAdvance = [];
        advance_params.forEach((i) => {
          if (ErrorInFormInput.includes(i)) {
            errorInAdvance.push(i);
          } else {
            errorInAdvance.filter((f) => f !== i);
          }
        });
        setErrorInAdvanceConfig(errorInAdvance);
      }
    } else {
      setErrorInAdvanceConfig([]);
    }
  }, [ErrorInFormInput.length]);

  return (
    <AdvancedConfigModal
      width="758px"
      toggleButton={
        <div ref={advanceConfigRef} style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          <TextLink type="standAlone">Open advanced options</TextLink>
          {isRoiListFetching && <Loader active={true} fullscreen={false} />}
        </div>
      }
      surface="light"
      fullScreenDialog={isModalFullScreen}
      disableAnimation={false}
      disableOutsideClick={true}
      ariaLabel="Testing Modal"
      opened={isOpen}
      onOpenedChange={(opened) => {
        if (isRoiListFetching && opened) return;
        setIsOpen(opened);
      }}
      hideCloseButton={true}
      closeButton={
      <Box
        component="button"
        type="button"
        sx={{
          cursor: "pointer",
          position: "absolute",
          right: "25px",
          top: "25px",
          background: "none",
          border: "none",
          padding: 0
        }}
        onClick={handleCloseOrCancel}
      >
        <Icon name="close" />
      </Box>
      }
    >
      <Title size="large" bold={true}>Advanced configuration</Title>
      <ModalBody>
        <MainSection
          cvAppName={cvAppName}
          cvappid={ids?.appid}
          data={schemaData}
          parameterKeyName="deploy-parameters"
          advancedData={advancedData}
          setAdvancedData={setAdvancedData}
          setIsTampered={setIsAdvanceConfigTampered}
          setErrorInFormInput={setErrorInFormInput}
          setIsClick={setIsClick}
          isClick={isClick}
          action={action}
          originalDeployConfig={originalDeployConfig}
          siteId={siteId}
        />

        {
        //Commenting this logic since the reset button is not in Figma 
        /* <div style={{ paddingTop: "20px" }}>
          <button
            type="button"
            data-testid="advancedConfig_resetToDefaultButton"
            onClick={() => {
              resetFunc();
              setIsClick(true);
            }}
            className={
              !isAdvanceConfigTampered
                ? "vision_add-config-reset-button vision_add-config-reset-button-disabled"
                : "vision_add-config-reset-button"
            }
          >
            Reset to default
          </button>
        </div> */}
      </ModalBody>
      <ModalFooter
        buttonData={{
          primary: {
            children: "Submit",
            onClick: () => {
              submitAdvancedConfiguration(
                advancedData,
                activeSession,
                ids,
                setIsOpen
              );
            },
            disabled: errorInAdvanceConfig.length !== 0 || (Array.isArray(ErrorInFormInput) && (ErrorInFormInput.includes("numberInputError") || ErrorInFormInput.includes("appearances"))),
            className:
              (errorInAdvanceConfig.length === 0 && !(Array.isArray(ErrorInFormInput) && (ErrorInFormInput.includes("numberInputError") || ErrorInFormInput.includes("appearances"))))
                ? "vision_add-config-submit-button"
                : "vision_add-config-submit-button-disabled",
          },
          close: {
            children: "Cancel",
            onClick: handleCloseOrCancel
          },
        }}
      />
    </AdvancedConfigModal>
  );
};

export default AdvancedConfiguration;
